import { createFileRoute, Link } from '@tanstack/react-router'
import type { EmailTemplateData } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import { Switch } from '@workspace/ui/components/switch'
import { Pencil } from 'lucide-react'
import {
  useAdminEmailTemplatesQuery,
  useAdminUpdateEmailTemplateMutation,
} from '../../../features/admin/hooks/use-admin-api'

export const Route = createFileRoute('/admin/email-templates/')({
  component: Page,
})

function EmailTemplateRow({ template }: { template: EmailTemplateData }) {
  const updateMutation = useAdminUpdateEmailTemplateMutation(template.emailType)

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-input/10 px-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{template.label}</span>
          {!template.editable && (
            <Badge variant="outline" size="sm">
              Dynamic
            </Badge>
          )}
        </div>
        <p className="truncate text-sm text-muted-foreground">{template.description}</p>
      </div>
      <div className="flex shrink-0 items-center gap-4">
        <Switch
          checked={template.enabled}
          disabled={updateMutation.isPending}
          onCheckedChange={(enabled) =>
            updateMutation.mutate({ path: { type: template.emailType }, body: { enabled } })
          }
        />
        <Link
          to="/admin/email-templates/$type"
          params={{ type: template.emailType }}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <Pencil className="size-3.5" />
          Edit
        </Link>
      </div>
    </div>
  )
}

function Page() {
  const templatesQuery = useAdminEmailTemplatesQuery()
  const templates = templatesQuery.data?.data?.items ?? []

  return (
    <div className="w-full space-y-4">
      <div>
        <h1 className="text-lg font-bold">Email Templates</h1>
        <p className="text-sm text-muted-foreground">
          Edit the subject and body of automated lifecycle emails, and toggle each on or off.
        </p>
      </div>

      {templatesQuery.isLoading ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          Loading...
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((template) => (
            <EmailTemplateRow key={template.emailType} template={template} />
          ))}
        </div>
      )}
    </div>
  )
}
